import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBooleanToggleField,
  AdminBlogShell,
  AdminEventEditorFields,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { validateYowanAdminEventDateTimeRanges } from './AdminEventCreatePage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  canReactivateYowanSeminarRecord,
  fetchYowanAdminClassDetail,
  fetchYowanAdminFacilityList,
  fetchYowanAdminInstructorList,
  fetchYowanAdminOrganizerList,
  fetchYowanAdminRoomList,
  fetchYowanAdminSeminarDetail,
  getYowanAdminPublicVisibilityStatusUi,
  getYowanAdminMutationErrorMessage,
  getYowanApiFieldErrorMessage,
  getYowanPublicVisibilityDiagnostics,
  getYowanPublicVisibilityMessages,
  getYowanSeminarReactivateIssues,
  getYowanSeminarSlotWarningIssues,
  toDateTimeLocalInputValue,
  updateYowanAdminClass,
  updateYowanAdminSeminar,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const ADMIN_EVENT_OPTION_PAGE_SIZE = 200;

const secondaryActionButtonStyle = {
  background: 'transparent',
  border: '1px solid currentColor',
  color: 'inherit',
};

const PublicVisibilityPanel = ({ visibility }) => {
  const statusUi = getYowanAdminPublicVisibilityStatusUi(visibility);
  const messages = getYowanPublicVisibilityMessages(visibility);
  const diagnostics = getYowanPublicVisibilityDiagnostics(visibility);

  return (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">公開面表示判定</p>
        <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
          {statusUi.label}
        </span>
      </div>
      {diagnostics.length ? (
        <dl className="yowan-admin-event-detail__definition">
          {diagnostics.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {messages.length ? (
        <ul className="yowan-admin-public-visibility__list">
          {messages.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : (
        <p className="yowan-admin-public-visibility__message">
          公開面に表示できる状態です。申込受付の可否は「申込受付中」と開催コマごとの受付状態で別に制御します。
        </p>
      )}
    </section>
  );
};

const AdminStateNoticePanel = ({ title, issues = [] }) => (
  issues.length ? (
    <section className="yowan-admin-public-visibility">
      <div className="yowan-admin-public-visibility__header">
        <p className="yowan-admin-public-visibility__title">{title}</p>
      </div>
      <ul className="yowan-admin-public-visibility__list">
        {issues.map((issue) => (
          <li key={issue.code || issue.label}>{issue.message}</li>
        ))}
      </ul>
    </section>
  ) : null
);

const buildOption = (value, label, option = {}) => ({
  value: value || '',
  label: label || '未設定',
  ...option,
  disabled: option.disabled === true,
});

const isInactiveMaster = (row = {}) => row.isActive === false || row.is_active === false;

const buildMasterOption = (row = {}, label, option = {}) => {
  const isInactiveRetained = row.retained === true && isInactiveMaster(row);

  return buildOption(
    row.id,
    `${label || row.id || '未設定'}${isInactiveRetained ? '（停止中・現在値）' : ''}`,
    { ...option, disabled: isInactiveRetained },
  );
};

const buildOrganizerOptions = (rows = []) => [
  buildOption('', '主催者を選択'),
  ...rows.map((row) => buildMasterOption(row, row.organizerName || row.organizer_name || row.representativeName || row.representative_name || row.id)),
];

const buildInstructorOptions = (rows = []) => [
  buildOption('', '講師を選択'),
  ...rows.map((row) => buildMasterOption(row, row.instructorName || row.instructor_name || row.memberName || row.member_name || row.name || row.id)),
];

const buildFacilityOptions = (rows = []) => [
  buildOption('', '未選択'),
  ...rows.map((row) => buildMasterOption(row, row.facilityName || row.facility_name || row.shortName || row.short_name || row.id)),
];

const buildRoomOptions = (rows = []) => [
  buildOption('', '未選択'),
  ...rows.map((row) => buildMasterOption(
    row,
    [row.facilityName || row.facility_name, row.roomName || row.room_name].filter(Boolean).join(' / ') || row.id,
    { facilityId: row.facilityId || row.facility_id || row.facility?.id || '' },
  )),
];

const buildInitialMasterOptions = (baseOptions = {}) => ({
  ...baseOptions,
  organizers: buildOrganizerOptions([]),
  instructors: buildInstructorOptions([]),
  facilities: buildFacilityOptions([]),
  rooms: buildRoomOptions([]),
});

const normalizeSeminarSeriesUiText = (value = '') => String(value || '')
  .replaceAll('一連の講習会', 'シリーズ')
  .replaceAll('単独講習会', '単独セミナー')
  .replaceAll('Seminars', 'セミナー')
  .replaceAll('Seminar', 'セミナー')
  .replaceAll('Series', 'シリーズ');

const mergeRowsById = (...groups) => {
  const rowsById = new Map();

  groups.flat().forEach((row) => {
    if (row?.id && !rowsById.has(row.id)) {
      rowsById.set(row.id, row);
    }
  });

  return Array.from(rowsById.values());
};

const getReferencedOrganizerRows = (record = {}) => [
  record.organizer ? { ...record.organizer, retained: true } : null,
  record.series?.organizer ? { ...record.series.organizer, retained: true } : null,
].filter((row) => row?.id);

const getReferencedInstructorRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.instructor ? { ...schedule.instructor, retained: true } : null),
  ...(record.slots || []).map((slot) => {
    const instructor = slot.instructorProfile || slot.instructor;
    return instructor ? { ...instructor, retained: true } : null;
  }),
].filter((row) => row?.id);

const getReferencedFacilityRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.facility ? { ...schedule.facility, retained: true } : null),
  ...(record.slots || []).map((slot) => slot.facility ? { ...slot.facility, retained: true } : null),
].filter((row) => row?.id);

const getReferencedRoomRows = (record = {}) => [
  ...(record.schedules || []).map((schedule) => schedule.room ? { ...schedule.room, retained: true } : null),
  ...(record.slots || []).map((slot) => slot.room ? { ...slot.room, retained: true } : null),
].filter((row) => row?.id);

const markMissingReferencedRowsInactive = (referencedRows = [], activeRows = []) => {
  const activeIds = new Set(activeRows.map((row) => row?.id).filter(Boolean));

  return referencedRows.map((row) => {
    const isMissingFromActiveList = row?.id && !activeIds.has(row.id);

    return {
      ...row,
      retained: true,
      isActive: isInactiveMaster(row) || isMissingFromActiveList ? false : row.isActive,
    };
  });
};

const SeminarSeriesFields = ({ content, register, watch, record, preservedDomainSearch = '', readOnly = false }) => {
  const labels = content?.form?.labels || {};
  const options = content?.form?.options || {};
  const seriesKind = watch?.('seriesKind') || 'standalone';
  const isGrouped = seriesKind === 'grouped';
  const seriesKindLabel = normalizeSeminarSeriesUiText(options.seriesKind?.find((option) => option.value === seriesKind)?.label || seriesKind || '-');
  const seriesRecord = record?.series || {};
  const seriesEditHref = seriesRecord.id
    ? appendYowanPreviewQueryIfNeeded(`/admin/events/series/${seriesRecord.id}/edit`, preservedDomainSearch)
    : '';

  if (!labels.seriesKind) {
    return null;
  }

  return (
    <section className="yowan-admin-event-series-fields">
      <header className="yowan-admin-event-series-fields__header">
        <h2 className="yowan-admin-event-series-fields__title">セミナーの構成</h2>
        <p className="yowan-admin-event-series-fields__lead">
          公開面で単独セミナーとして扱うか、シリーズとして複数セミナーをまとめるかを確認・更新します。
        </p>
      </header>

      <div className="c-form__field">
        <span className="c-form__label">{labels.seriesKind}</span>
        {readOnly ? (
          <>
            <input type="hidden" {...register('seriesKind')} />
            <span className="yowan-admin-event-series-fields__readonly">{seriesKindLabel}</span>
          </>
        ) : (
          <div className="c-form__choices">
            {(options.seriesKind || []).map((option) => (
              <label key={option.value} className="c-form__choice">
                <input type="radio" value={option.value} {...register('seriesKind')} />
                <span>{normalizeSeminarSeriesUiText(option.label)}</span>
              </label>
            ))}
          </div>
        )}
        {content.form.seriesKindHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesKindHelpText}</p> : null}
      </div>

      {readOnly && isGrouped && labels.publicSlug ? (
        <div className="c-form__field">
          <span className="c-form__label">{labels.publicSlug}</span>
          <span className="yowan-admin-event-series-fields__readonly">{seriesRecord.publicSlug || record?.publicSlug || '-'}</span>
          <p className="yowan-admin-form__field-note">
            シリーズの公開URL slugです。変更は
            {seriesEditHref ? <Link to={seriesEditHref}>シリーズ編集</Link> : 'シリーズ編集'}
            で行います。
          </p>
        </div>
      ) : null}

      {isGrouped ? (
        <div className="yowan-admin-event-series-fields__grid">
          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesTitle}</span>
            <input className="c-form__control" type="text" {...register('seriesTitle')} />
            {content.form.seriesTitleHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesTitleHelpText}</p> : null}
          </label>

          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesSubtitle}</span>
            <input className="c-form__control" type="text" {...register('seriesSubtitle')} />
            {content.form.seriesSubtitleHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesSubtitleHelpText}</p> : null}
          </label>

          <div className="c-form__field">
            <span className="c-form__label">{labels.seriesOrganizerId}</span>
            <select className="c-form__control" {...register('seriesOrganizerId')}>
              {(options.organizers || []).map((option) => (
                <option key={option.value || 'empty'} value={option.value} disabled={option.disabled === true}>
                  {option.label}
                </option>
              ))}
            </select>
            {content.form.seriesOrganizerHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesOrganizerHelpText}</p> : null}
          </div>

          <label className="c-form__field">
            <span className="c-form__label">{labels.seriesFullParticipationFee}</span>
            <input className="c-form__control" type="number" min="0" step="100" {...register('seriesFullParticipationFee')} />
            {content.form.seriesFullParticipationFeeHelpText ? <p className="yowan-admin-form__field-note">{content.form.seriesFullParticipationFeeHelpText}</p> : null}
          </label>
        </div>
      ) : null}
    </section>
  );
};

const classToFormValues = (record = {}, fallbackDefaults = {}) => ({
  ...fallbackDefaults,
  id: record.id || '',
  title: record.title || '',
  publicSlug: record.publicSlug || '',
  eventType: 'regular',
  organizerId: record.organizerId || '',
  isPublished: record.isPublished === true,
  isActive: record.isActive !== false,
  calendarUrl: record.calendarUrl || '',
  showInstructorProfiles: record.showInstructorProfiles === true,
  summary: record.summary || '',
  guidance: record.guidance || '',
  detail_top_image_id: record.detail_top_image_id || record.detail_top_image?.id || record.detailTopImage?.id || '',
  detailTopImage: '',
  primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  image: '',
  schedules: (record.schedules || []).length
    ? record.schedules.map((schedule) => ({
        id: schedule.id || '',
        scheduleKind: schedule.scheduleKind || 'weekly',
        scheduleLabel: schedule.scheduleLabel || '',
        weekday: schedule.weekday || 'monday',
        startTime: schedule.startTime || '',
        endTime: schedule.endTime || '',
        facilityId: schedule.facilityId || schedule.facility_id || schedule.facility?.id || schedule.room?.facility?.id || '',
        roomId: schedule.roomId || schedule.room_id || schedule.room?.id || '',
        locationIsPublic: schedule.locationIsPublic === false ? 'false' : 'true',
        publicLocationLabel: schedule.publicLocationLabel || '',
        publicLocationNote: schedule.publicLocationNote || '',
        privateLocationNote: [schedule.privateLocationNote, schedule.remarks].filter(Boolean).join('\n'),
        capacity: schedule.capacity ?? '',
        fee: schedule.fee ?? '',
        instructorId: schedule.instructorId || '',
        isActive: schedule.isActive !== false,
        remarks: '',
      }))
    : fallbackDefaults.schedules,
});

const seminarToFormValues = (record = {}, fallbackDefaults = {}) => ({
  ...fallbackDefaults,
  id: record.id || '',
  title: record.title || '',
  publicSlug: record.publicSlug || record.series?.publicSlug || '',
  subtitle: record.subtitle || '',
  seriesId: record.series?.id || '',
  seriesKind: record.seriesKind === 'grouped' ? 'grouped' : 'standalone',
  seriesTitle: record.series?.title || '',
  seriesSubtitle: record.series?.subtitle || '',
  seriesOrganizerId: record.series?.organizerId || record.organizerId || '',
  seriesFullParticipationFee: record.series?.fullParticipationFee ?? '',
  eventType: 'spot',
  organizerId: record.organizerId || '',
  isPublished: record.isPublished === true,
  acceptsApplications: record.acceptsApplications !== false,
  isActive: record.isActive !== false,
  summary: record.summary || '',
  guidance: record.guidance || '',
  fullParticipationFee: record.seminarFullParticipationFee ?? '',
  primary_image_id: record.primary_image_id || record.primary_image?.id || '',
  image: '',
  slots: (record.slots || []).length
    ? record.slots.map((slot) => ({
        id: slot.id || '',
        title: slot.title || '',
        startAt: toDateTimeLocalInputValue(slot.startAt),
        endAt: toDateTimeLocalInputValue(slot.endAt),
        facilityId: slot.facilityId || slot.facility_id || slot.facility?.id || slot.room?.facility?.id || '',
        roomId: slot.roomId || slot.room_id || slot.room?.id || '',
        locationDisplayMode: slot.locationDisplayMode || 'facility',
        publicLocationLabel: slot.publicLocationLabel || '',
        publicLocationNote: slot.publicLocationNote || '',
        privateLocationNote: slot.privateLocationNote || '',
        thingsToBring: slot.thingsToBring || slot.things_to_bring || '',
        capacity: slot.capacity ?? '',
        slotFee: slot.slotFee ?? '',
        instructorId: slot.instructorProfileId || slot.instructorId || '',
        showInstructorProfile: slot.showInstructorProfile === true,
        isOpen: slot.isOpen === true,
        isActive: slot.isActive !== false,
      }))
    : fallbackDefaults.slots,
});

const buildFormContent = ({
  baseContent,
  isSeminar,
  options,
}) => {
  const labels = isSeminar
    ? {
        ...baseContent.form.labels,
        title: 'セミナー名',
        publicSlug: baseContent.form.labels.publicSlug,
        seriesKind: baseContent.form.labels.seriesKind,
        seriesTitle: baseContent.form.labels.seriesTitle,
        seriesSubtitle: baseContent.form.labels.seriesSubtitle,
        seriesOrganizerId: baseContent.form.labels.seriesOrganizerId,
        seriesFullParticipationFee: baseContent.form.labels.seriesFullParticipationFee,
        weekday: undefined,
        calendarUrl: undefined,
        showInstructorProfiles: undefined,
        detailTopImage: undefined,
        scheduleRuleJson: undefined,
        isActive: undefined,
        image: '画像',
      }
    : {
        ...baseContent.form.labels,
        title: '教室名',
        publicSlug: baseContent.form.labels.publicSlug,
        subtitle: undefined,
        seriesKind: undefined,
        seriesTitle: undefined,
        seriesSubtitle: undefined,
        seriesOrganizerId: undefined,
        seriesFullParticipationFee: undefined,
        fullParticipationFee: undefined,
        scheduleRuleJson: undefined,
      };

  return {
    ...baseContent,
    form: {
      ...baseContent.form,
      labels,
      options: {
        ...baseContent.form.options,
        ...options,
        eventType: isSeminar
          ? [{ value: 'spot', label: '単発セミナー' }]
          : [{ value: 'regular', label: '常設クラス' }],
        locationDisplayModes: isSeminar
          ? baseContent.form.options.locationDisplayModes
          : baseContent.form.options.classLocationDisplayModes || baseContent.form.options.locationDisplayModes,
      },
      eventTypeLocked: true,
      imageInputType: 'file',
      summaryHelpText: baseContent.form.summaryHelpText,
      summaryMaxLength: baseContent.form.summaryMaxLength,
      guidanceHelpText: baseContent.form.guidanceHelpText,
      guidanceMaxLength: baseContent.form.guidanceMaxLength,
      detailTopImageHelpText: isSeminar ? undefined : baseContent.form.detailTopImageHelpText,
      imageHelpText: baseContent.form.imageHelpText,
      isPublishedHelpText: isSeminar
        ? baseContent.form.isPublishedHelpText
        : '公開する場合は、公開状態と有効状態の両方を確認してください。',
      isActiveHelpText: isSeminar
        ? baseContent.form.isActiveHelpText
        : '停止した教室は公開面や新規受付の対象から外します。',
      standardSlotLabels: undefined,
      scheduleLabels: isSeminar ? undefined : baseContent.form.scheduleLabels,
      slotLabels: isSeminar ? baseContent.form.slotLabels : undefined,
      submitLabel: isSeminar ? 'セミナーを更新' : '教室を更新',
      pendingMessage: '保存処理を受け付けました。',
    },
  };
};

const AdminEventEditPage = () => {
  const baseContent = yowanPageContent['/admin/events/:eventId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const queryKind = new URLSearchParams(location.search).get('kind');
  const [recordKind, setRecordKind] = useState(queryKind === 'class' ? 'class' : queryKind === 'seminar' ? 'seminar' : '');
  const [currentRecord, setCurrentRecord] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [masterOptions, setMasterOptions] = useState(() => buildInitialMasterOptions(baseContent?.form?.options || {}));
  const [slotIndexes, setSlotIndexes] = useState([0]);
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: baseContent?.form?.defaults,
    shouldUnregister: true,
  });

  useEffect(() => {
    if (queryKind === 'class' || queryKind === 'seminar') {
      setRecordKind(queryKind);
    }
  }, [queryKind]);

  useEffect(() => {
    if (!baseContent || !eventId) {
      return undefined;
    }

    let isMounted = true;

    const loadMastersAndDetail = async () => {
      setIsLoading(true);
      setLoadError('');
      setSubmitMessage('');

      try {
        const [
          organizers,
          instructors,
          facilities,
          rooms,
        ] = await Promise.all([
          fetchYowanAdminOrganizerList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminInstructorList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminFacilityList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
          fetchYowanAdminRoomList({ pageSize: ADMIN_EVENT_OPTION_PAGE_SIZE, isActive: true }),
        ]);

        let detail;
        let nextKind = recordKind;

        if (recordKind === 'class') {
          detail = await fetchYowanAdminClassDetail(eventId);
          nextKind = 'class';
        } else if (recordKind === 'seminar') {
          detail = await fetchYowanAdminSeminarDetail(eventId);
          nextKind = 'seminar';
        } else {
          try {
            detail = await fetchYowanAdminClassDetail(eventId);
            nextKind = 'class';
          } catch (classError) {
            detail = await fetchYowanAdminSeminarDetail(eventId);
            nextKind = 'seminar';
          }
        }

        if (!isMounted) {
          return;
        }

        setCurrentRecord(detail);
        setRecordKind(nextKind);
        const activeOrganizers = organizers.results || [];
        const activeInstructors = instructors.results || [];
        const activeFacilities = facilities.results || [];
        const activeRooms = rooms.results || [];
        const options = {
          organizers: buildOrganizerOptions(mergeRowsById(activeOrganizers, markMissingReferencedRowsInactive(getReferencedOrganizerRows(detail), activeOrganizers))),
          instructors: buildInstructorOptions(mergeRowsById(activeInstructors, markMissingReferencedRowsInactive(getReferencedInstructorRows(detail), activeInstructors))),
          facilities: buildFacilityOptions(mergeRowsById(activeFacilities, markMissingReferencedRowsInactive(getReferencedFacilityRows(detail), activeFacilities))),
          rooms: buildRoomOptions(mergeRowsById(activeRooms, markMissingReferencedRowsInactive(getReferencedRoomRows(detail), activeRooms))),
        };
        setMasterOptions({
          ...baseContent.form.options,
          ...options,
        });

        const formValues = nextKind === 'class'
          ? classToFormValues(detail, yowanPageContent['/admin/events/classes/new']?.form?.defaults || {})
          : seminarToFormValues(detail, yowanPageContent['/admin/events/new']?.form?.defaults || {});

        reset(formValues);
        setSlotIndexes(Array.from({ length: formValues.slots?.length || formValues.schedules?.length || 1 }, (_, index) => index));
      } catch (error) {
        if (isMounted) {
          setCurrentRecord(null);
          setLoadError('Events情報を読み込めませんでした。時間をおいて再度お試しください。');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMastersAndDetail();

    return () => {
      isMounted = false;
    };
  }, [baseContent, eventId, recordKind, reset]);

  if (!baseContent) {
    return null;
  }

  const isSeminar = recordKind !== 'class';
  const formContent = buildFormContent({
    baseContent,
    isSeminar,
    options: masterOptions,
  });
  const listHref = isSeminar ? '/admin/events/seminars' : '/admin/events/classes';
  const headerTitle = isSeminar ? 'セミナー編集' : '教室編集';
  const headerParagraphs = isSeminar
    ? ['単発セミナーの内容、画像、公開状態、開催コマを編集します。']
    : ['常設クラスの教室情報、画像、公開状態、開催情報を編集します。'];
  const watchedIsActive = watch('isActive');
  const includesCancelOperation = isSeminar && currentRecord?.isActive !== false && watchedIsActive === false;
  const cancelHref = currentRecord?.cancelHref || (currentRecord?.id ? `/admin/events/${currentRecord.id}/cancel` : '');
  const cancelRouteHref = cancelHref ? appendYowanPreviewQueryIfNeeded(cancelHref, preservedDomainSearch) : '';
  const canReactivateSeminar = isSeminar && canReactivateYowanSeminarRecord(currentRecord);
  const slotWarningIssues = isSeminar ? getYowanSeminarSlotWarningIssues(currentRecord || {}) : [];
  const reactivateIssues = isSeminar ? getYowanSeminarReactivateIssues(currentRecord || {}) : [];
  const reactivateRouteHref = canReactivateSeminar
    ? appendYowanPreviewQueryIfNeeded(currentRecord.reactivateHref || `/admin/events/${currentRecord.id}/reactivate`, preservedDomainSearch)
    : '';
  const cancelRedirectHelpText = baseContent.cancelAction?.editRedirectHelpText ||
    'この操作はセミナー中止処理を含みます。通常の編集保存では実行できません。タイトル・本文・画像などの通常項目を変更したい場合は、先に有効状態のまま更新してください。';
  const reactivateRedirectHelpText = baseContent.reactivateAction?.editRedirectHelpText ||
    'このセミナーは運用再開または公開面復旧の確認が必要です。この編集画面ではタイトル・本文・画像・公開設定・申込受付・開催コマだけを保存し、運用再開処理は実行しません。運用を戻す場合は、専用ページで公開・申込受付・開催コマの現在値を確認してから実行してください。';

  const onSubmit = async (values) => {
    if (includesCancelOperation) {
      setSubmitMessage('この操作はセミナー中止処理を含むため、専用ページで確認してください。');
      return;
    }

    const invalidDateTimeRangeMessage = formContent.form.errors.invalidDateTimeRange ||
      '終了日時は開始日時以降に設定してください。';

    if (!validateYowanAdminEventDateTimeRanges({
      values,
      setError,
      clearErrors,
      message: invalidDateTimeRangeMessage,
    })) {
      setSubmitMessage(invalidDateTimeRangeMessage);
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage(formContent.form.pendingMessage);

    try {
      if (isSeminar) {
        const seminarValues = { ...values };

        delete seminarValues.publicSlug;
        seminarValues.isActive = currentRecord?.isActive !== false;

        await updateYowanAdminSeminar(eventId, seminarValues);
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/seminars', preservedDomainSearch));
      } else {
        const classValues = { ...values };

        delete classValues.publicSlug;
        await updateYowanAdminClass(eventId, classValues);
        navigate(appendYowanPreviewQueryIfNeeded('/admin/events/classes', preservedDomainSearch));
      }
    } catch (error) {
      const publicSlugMessage = getYowanApiFieldErrorMessage(error, ['public_slug', 'series_public_slug', 'publicSlug']);

      if (publicSlugMessage) {
        setError('publicSlug', { type: 'server', message: publicSlugMessage });
        setSubmitMessage('公開URL slugを確認してください。');
      } else {
        setSubmitMessage(getYowanAdminMutationErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleAddSlot = () => {
    const nextIndex = slotIndexes.length ? Math.max(...slotIndexes) + 1 : 0;
    const nextPosition = slotIndexes.length;

    if (isSeminar) {
      setValue(`slots.${nextPosition}.title`, '');
      setValue(`slots.${nextPosition}.locationDisplayMode`, 'facility');
      setValue(`slots.${nextPosition}.showInstructorProfile`, false);
      setValue(`slots.${nextPosition}.isOpen`, true);
      setValue(`slots.${nextPosition}.isActive`, true);
    } else {
      setValue(`schedules.${nextPosition}.scheduleKind`, 'weekly');
      setValue(`schedules.${nextPosition}.weekday`, 'tuesday');
      setValue(`schedules.${nextPosition}.locationIsPublic`, 'true');
      setValue(`schedules.${nextPosition}.isActive`, true);
    }

    setSlotIndexes([...slotIndexes, nextIndex]);
  };
  const handleRemoveSlot = (slotIndex) => {
    setSlotIndexes((currentIndexes) => {
      if (currentIndexes.length <= 1) {
        return currentIndexes;
      }

      return currentIndexes.filter((currentIndex) => currentIndex !== slotIndex);
    });
  };

  return (
    <AdminBlogShell
      activeNavHref={listHref}
      routeKey="/admin/events/:eventId/edit"
    >
      <AdminPageHeader
        title={headerTitle}
        paragraphs={headerParagraphs}
        actions={[
          <Link
            key="back-to-list"
            to={appendYowanPreviewQueryIfNeeded(listHref, preservedDomainSearch)}
          >
            一覧へ戻る
          </Link>,
          isSeminar ? (
            <Link key="event-applications" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${eventId}/applications`, preservedDomainSearch)}>
              参加者一覧
            </Link>
          ) : (
            <Link key="class-applications" to={appendYowanPreviewQueryIfNeeded(`/admin/events/classes/${eventId}/applications`, preservedDomainSearch)}>
              申込者一覧
            </Link>
          ),
        ]}
      />

      <form className="yowan-admin-form yowan-admin-event-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
        {isLoading ? <LoadingSpinner label="Events情報を読み込み中" showLabel /> : null}
        {!isLoading && loadError ? <p className="yowan-admin-form__message">{loadError}</p> : null}
        {!isLoading && !loadError ? (
          <>
            {isSeminar ? (
              <SeminarSeriesFields
                content={formContent}
                register={register}
                watch={watch}
                record={currentRecord}
                preservedDomainSearch={preservedDomainSearch}
                readOnly
              />
            ) : null}
            <AdminEventEditorFields
              content={formContent}
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              slotIndexes={slotIndexes}
              onAddSlot={handleAddSlot}
              onRemoveSlot={handleRemoveSlot}
              currentImage={currentRecord?.primary_image}
              currentDetailTopImage={currentRecord?.detail_top_image || currentRecord?.detailTopImage}
              hidePublicSlug={isSeminar && currentRecord?.seriesKind === 'grouped'}
              publicSlugDisabled
              publicPathPreview={currentRecord?.publicPath}
              canonicalUrlPreview={currentRecord?.canonicalUrl}
              publicSlugNote={isSeminar && currentRecord?.seriesKind === 'grouped'
                ? 'シリーズの公開URL slugです。変更はシリーズ編集画面で行います。'
                : formContent.form.publicSlugReadOnlyHelpText}
              renderAfterPublished={() => (isSeminar && formContent.form.labels.acceptsApplications ? (
                <AdminBooleanToggleField
                  label={formContent.form.labels.acceptsApplications}
                  name="acceptsApplications"
                  register={register}
                  helpText={formContent.form.acceptsApplicationsHelpText}
                />
              ) : null)}
            />
            {isSeminar && currentRecord?.publicVisibility ? (
              <PublicVisibilityPanel visibility={currentRecord.publicVisibility} />
            ) : null}
            {isSeminar ? (
              <AdminStateNoticePanel title="開催コマの注意" issues={slotWarningIssues} />
            ) : null}
            {canReactivateSeminar ? (
              <section className="yowan-admin-detail-panel">
                <header className="yowan-admin-event-detail__section-header">
                  <h2 className="yowan-admin-detail-panel__title">セミナー運用再開の確認が必要です</h2>
                  <p className="yowan-admin-detail-panel__lead">
                    この編集画面で通常項目を保存しても、運用停止状態や単独セミナーの内部親シリーズ停止状態は解除されません。
                  </p>
                </header>
                <AdminStateNoticePanel title="運用再開が必要な理由" issues={reactivateIssues} />
                <p className="yowan-admin-form__field-note">{reactivateRedirectHelpText}</p>
                <div className="yowan-admin-form__actions">
                  <Link className="c-form__submit" to={reactivateRouteHref}>
                    運用再開専用ページで確認する
                  </Link>
                </div>
              </section>
            ) : null}
          </>
        ) : null}

        {includesCancelOperation ? (
          <section className="yowan-admin-detail-panel">
            <header className="yowan-admin-event-detail__section-header">
              <h2 className="yowan-admin-detail-panel__title">セミナー中止の確認が必要です</h2>
              <p className="yowan-admin-detail-panel__lead">
                「有効にする」を外したため、この操作はセミナー中止処理を含みます。
              </p>
            </header>
            <p className="yowan-admin-form__field-note">{cancelRedirectHelpText}</p>
            <div className="yowan-admin-form__actions">
              <Link className="c-form__submit" to={cancelRouteHref}>
                中止専用ページで確認する
              </Link>
              <button
                type="button"
                className="c-form__submit"
                style={secondaryActionButtonStyle}
                onClick={() => setValue('isActive', true)}
              >
                有効状態に戻す
              </button>
            </div>
          </section>
        ) : (
          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting || isLoading || Boolean(loadError)}>
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span>{formContent.form.pendingMessage}</span>
                </>
              ) : formContent.form.submitLabel}
            </button>
          </div>
        )}

        {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
      </form>
    </AdminBlogShell>
  );
};

export default AdminEventEditPage;
